import { Box, Typography, useTheme } from "@mui/material";
import InfoCircle from "../../../../../media/images/icons/info-circle";
import TransactionCard from "./TransactionCard";
import BottomSheetLayout from "../../../../components/BottomSheetLayout";
import { useEffect, useState } from "react";
import useSWRMutation from "swr/mutation";
import { fetcherPost } from "../../../../../services/fetcher";
import { ITransaction } from "../../../../../types/wallet";
import { useAppDispatch } from "../../../../../hooks/useRedux";
import {
  setOpenSnackbar,
  setSnackbarDescription,
  setSnackbarErrorStatus,
} from "../../../../../redux/features/snackbarSlice";
import CustomButton from "../../../../components/CustomButton";
import CustomInput from "../../../../components/CustomInput";
import SearchSVG from "../../../../../media/images/icons/search";
import { PreventInputAlpha } from "../../../../../utils/functions";
import LoadingBox from "../../../../components/LoadingBox";
import EmptyListBox from "../../../../components/EmptyListBox";
import { useInView } from "react-intersection-observer";
import { DotLoader } from "react-spinner-overlay";

interface Props {
  open: boolean;
  onDismiss: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function Transactions({ open, onDismiss }: Props) {
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const [transactions, setTransactions] = useState<ITransaction[]>([]);

  const [transactionCode, setTransactionCode] = useState("");

  const [pageN, setPageN] = useState(1);
  const [pageCounts, setPageCounts] = useState(0);

  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0,
  });

  const { trigger: getTransactions, isMutating: isGetTransactionsLoading } =
    useSWRMutation(
      [`/Invoice/Search`, localStorage.getItem("access_token")],
      fetcherPost,
      {
        onSuccess: (data) => {
          setTransactions(data);
        },
      }
    );
  const {
    trigger: getTransactionsNextPage,
    isMutating: isGetTransactionsNextPageLoading,
  } = useSWRMutation(
    [`/Invoice/Search`, localStorage.getItem("access_token")],
    fetcherPost,
    {
      onSuccess: (data) => {
        setTransactions([...transactions, ...data.data]);
      },
    }
  );

  useEffect(() => {
    if (transactionCode) {
      const timer = setTimeout(() => {
        getTransactions({
          code: transactionCode,
          pageNr: 1,
        })
          .then((data) => {
            setTransactions(data.data);
          })
          .catch((e) => {
            console.log(e);
          });
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      getTransactions({
        code: 0,
        pageNr: 1,
      });
    }
  }, [transactionCode]);

  useEffect(() => {
    if (inView && pageCounts > transactions.length) {
      setPageN((prev) => prev + 1);
    }
  }, [inView]);

  useEffect(() => {
    getTransactionsNextPage({
      pageNr: pageN,
    });
  }, [pageN]);

  useEffect(() => {
    if (open) {
      getTransactions({
        code: 0,
        pageNr: 1,
      });
    }
  }, [open]);

  const handleChangeTransactionCode = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTransactionCode(e.target.value);
  };

  return (
    <BottomSheetLayout height="100%" open={open} onDismiss={onDismiss}>
      <Box
        sx={{
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          flex: "1",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                color: "primary.main",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              تراکنش‌ها
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "3px",
              }}
            >
              <InfoCircle size={16} color={theme.palette.primary.main} />
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "primary.main",
                }}
              >
                برای مشاهده جزئیات هر تراکنش روی آن ضربه بزنید.
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          ></Box>
        </Box>
        <Box
          sx={{
            flex: "1",
            overflowY: "auto",
            paddingY: "8px",
            paddingX: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <CustomInput
            type={"numeric"}
            name={"smartCard"}
            value={transactionCode}
            handelChange={(e) =>
              PreventInputAlpha(e, handleChangeTransactionCode)
            }
            placHolder="جستجو شماره رهگیری"
            endAdornment={<SearchSVG color={theme.palette.primary.main} />}
          />
          {isGetTransactionsLoading ? (
            <LoadingBox />
          ) : !transactions.find((item) => item.status === 0) ? (
            transactions.map((item, index) => {
              if (item.status === 0) {
                return (
                  item.bankTrackId && (
                    <>
                      <TransactionCard ref={ref} {...item} />
                      {isGetTransactionsNextPageLoading &&
                        index === transactions.length - 1 && (
                          <Box
                            sx={{
                              minHeight: "200px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "16px",
                            }}
                          >
                            <DotLoader color={theme.palette.primary.main} />
                            <Typography>لطفا منتظر باشید...</Typography>
                          </Box>
                        )}
                    </>
                  )
                );
              } else {
                return (
                  <>
                    <TransactionCard ref={ref} {...item} />
                    {isGetTransactionsNextPageLoading &&
                      index === transactions.length - 1 && (
                        <Box
                          sx={{
                            minHeight: "200px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "16px",
                          }}
                        >
                          <DotLoader color={theme.palette.primary.main} />
                          <Typography>لطفا منتظر باشید...</Typography>
                        </Box>
                      )}
                  </>
                );
              }
            })
          ) : (
            <EmptyListBox description="تراکنشی وجود ندارد." />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          padding: "4px 8px",
          width: "100%",
        }}
      >
        <CustomButton
          width="100%"
          onClick={() => onDismiss(false)}
          title="بستن"
          bgColor="secondary.main"
          justifyContent="center"
          px="10px"
          py="10px"
          borderRadius="6px"
        />
      </Box>
    </BottomSheetLayout>
  );
}
